import { useEffect, useState, useRef } from "react";
import api from "../../services/api";

import editIcon from "../../assets/edit-icon.png";
import deleteIcon from "../../assets/delete-icon.png";

const COLORS = {
  primary: "#0D47A1",
  danger: "#D32F2F",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  text: "#1E1E1E",
  muted: "#757575",
};

const ProductTypes = () => {
  const [types, setTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formRef = useRef(null);

  const loadTypes = async () => {
    try {
      const res = await api.get("/product-types/list.php");
      setTypes(res.data?.data || []);
    } catch {
      setError("Failed to load product types");
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showForm && formRef.current && !formRef.current.contains(e.target)) {
        resetForm();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName("");
    setError("");
  };

  const validate = () => {
    if (!name.trim()) return "Type name required";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        await api.post("/product-types/update.php", {
          id: editingId,
          name: name.trim(),
        });
      } else {
        await api.post("/product-types/create.php", {
          name: name.trim(),
        });
      }

      resetForm();
      loadTypes();
    } catch {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type) => {
    setName(type.name);
    setEditingId(type.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product type?")) return;

    try {
      await api.post("/product-types/delete.php", { id });
      loadTypes();
    } catch {
      setError("Delete failed");
    }
  };

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Product Types</h2>
          <p style={styles.subtitle}>Manage product categories</p>
        </div>

        <button
          style={styles.addBtn}
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setName("");
            setError("");
          }}
        >
          + Add Type
        </button>
      </div>

      {/* FORM */}
      {showForm && (
        <div ref={formRef} style={styles.formCard}>
          <form onSubmit={submit} style={styles.formRow}>
            <div style={styles.field}>
              <label style={styles.label}>Type Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Grocery"
                style={styles.input}
              />
            </div>

            <div style={styles.buttonWrap}>
              <button type="submit" style={styles.saveBtn} disabled={loading}>
                {loading
                  ? editingId
                    ? "Updating..."
                    : "Adding..."
                  : editingId
                  ? "Update"
                  : "Save"}
              </button>
            </div>
          </form>

          {error && <p style={styles.error}>{error}</p>}
        </div>
      )}

      {/* TABLE */}
      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Type Name</th>
                <th style={styles.thCenter}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {types.length === 0 && (
                <tr>
                  <td colSpan="3" style={styles.empty}>
                    No product types
                  </td>
                </tr>
              )}

              {types.map((t, index) => (
                <tr key={t.id}>
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.tdBold}>{t.name}</td>

                  <td style={styles.actionCell}>
                    <div style={styles.iconWrapper}>
                      <button
                        style={styles.iconBtn}
                        onClick={() => handleEdit(t)}
                      >
                        <img src={editIcon} alt="edit" style={styles.icon} />
                      </button>

                      <button
                        style={styles.iconBtn}
                        onClick={() => handleDelete(t.id)}
                      >
                        <img src={deleteIcon} alt="delete" style={styles.icon} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ================= FIXED STYLES ================= */

const styles = {
  page: {
    background: COLORS.bg,
    minHeight: "100vh",
    padding: "clamp(12px, 4vw, 30px)",
    boxSizing: "border-box",
    overflowX: "hidden", // ✅ important fix
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 20,
  },

  title: {
    fontWeight: 700,
    fontSize: "clamp(18px, 2.5vw, 26px)",
    marginBottom: 4,
    color: COLORS.text,
  },

  subtitle: {
    fontSize: "clamp(12px, 2vw, 14px)",
    color: COLORS.muted,
  },

  addBtn: {
    background: COLORS.primary,
    color: "#fff",
    padding: "10px 16px",
    borderRadius: 10,
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
  },

  formCard: {
    background: COLORS.card,
    padding: "clamp(15px,3vw,25px)",
    borderRadius: 16,
    marginBottom: 20,
    border: `1px solid ${COLORS.border}`,
  },

formRow: {
  display: "grid",
  gridTemplateColumns: "1fr",   // mobile default
  gap: 12,
},

field: {
  width: "100%",
  display: "flex",
  flexDirection: "column",
},

  label: {
    fontSize: 13,
    marginBottom: 6,
    color: COLORS.muted,
  },

input: {
  width: "100%",
  padding: "12px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  boxSizing: "border-box",   // ✅ ADD THIS
},
buttonWrap: {
  display: "flex",
  alignItems: "flex-end",
  width: "100%",
},

saveBtn: {
  background: COLORS.primary,
  color: "#fff",
  border: "none",
  padding: "12px 16px",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
  width: "100%", // mobile full width
},

  card: {
    background: COLORS.card,
    borderRadius: 18,
    padding: "clamp(12px,2vw,25px)",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    // ❌ removed minWidth
  },

  th: {
    textAlign: "left",
    padding: "12px",
    fontSize: 14,
    color: COLORS.muted,
    borderBottom: `1px solid ${COLORS.border}`,
    whiteSpace: "nowrap",
  },

  thCenter: {
    textAlign: "center",
    padding: "12px",
    fontSize: 14,
    color: COLORS.muted,
    borderBottom: `1px solid ${COLORS.border}`,
  },

  td: {
    padding: "14px 12px",
    borderBottom: `1px solid ${COLORS.border}`,
  },

  tdBold: {
    padding: "14px 12px",
    fontWeight: 600,
    borderBottom: `1px solid ${COLORS.border}`,
  },

  actionCell: {
    borderBottom: `1px solid ${COLORS.border}`,
    textAlign: "center",
  },

  iconWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
  },

  iconBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },

  icon: {
    width: 18,
    height: 18,
  },

  empty: {
    textAlign: "center",
    padding: 20,
    color: COLORS.muted,
  },

  error: {
    marginTop: 10,
    color: COLORS.danger,
  },

  
};

export default ProductTypes;